import { isEmail, useForm } from "@mantine/form";

import type { Customer } from "../../../types";

import {
    addressSearchToAddress,
    addressToAddressSearch,
} from "../components/CollectCustomerInfo/CollectCustomerInfo.helpers";

export const DEFAULT_COUNTRY_CODE = "US";

export interface CollectCustomerInfoFormData {
    firstName: string;
    lastName: string;
    email: string;
    addressSearch: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
    countryCode: string;
}

export const useCollectCustomerInfoForm = (customer: Customer) => {
    const form = useForm<CollectCustomerInfoFormData>({
        validateInputOnBlur: true,
        initialValues: {
            firstName: customer.firstName || "",
            lastName: customer.lastName || "",
            email: customer.email || "",
            addressSearch: addressToAddressSearch(customer.address),
            address1: customer.address?.Address1 || "",
            city: customer.address?.City || "",
            state: customer.address?.StateProvinceCode || "",
            zip: customer.address?.PostalCode || "",
            countryCode: customer.address?.CountryCode || DEFAULT_COUNTRY_CODE,
        },
        validate: {
            firstName: (value, values) => {
                if (!value) return "First name is required";
                if (value.length > 48) return "First name must be 48 characters or less";
                if (values.firstName.length + values.lastName.length >= 50)
                    return "First name and last name must be less than 50 characters combined";
                return null;
            },
            lastName: (value, values) => {
                if (!value) return "Last name is required";
                if (value.length > 48) return "Last name must be 48 characters or less";
                if (values.firstName.length + values.lastName.length >= 50)
                    return "First name and last name must be less than 50 characters combined";
                return null;
            },
            email: isEmail("Invalid email"),
            address1: (value) => {
                if (!value) return "Address is required";
                return null;
            },
            city: (value) => {
                if (!value) return "City is required";
                return null;
            },
            state: (value) => {
                if (!value) return "State is required";
                if (value.length !== 2) return "State must be 2 characters";
                if (value.toUpperCase() !== value) return "State must only contain capital letters";
                return null;
            },
            zip: (value) => {
                if (!value) return "Zip code is required";
                if (value.length < 5) return "Zip code must be at least 5 characters";
                return null;
            },
            countryCode: (value) => {
                if (!value) return "Country code is required";
                if (value.length !== 2) return "Country code must be 2 characters";
                return null;
            },
        },
    });

    form.watch("addressSearch", ({ value }) => {
        form.setValues(addressSearchToAddress(value));
    });

    return form;
};
